import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { MessageSquare, ArrowLeft, Shield } from "lucide-react";

import type { Easing } from "framer-motion";

const ease: Easing = [0.25, 0.1, 0.25, 1];
const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i: number) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.5, ease } }),
};

const SECTIONS = [
  {
    title: "1. Dados Coletados",
    content: `Coletamos os seguintes tipos de dados:\n\n• **Dados de identificação:** nome, e-mail, telefone, empresa.\n• **Dados de uso:** mensagens processadas em grupos do WhatsApp (metadados e conteúdo para processamento pela IA).\n• **Dados de navegação:** IP, cookies, dados do dispositivo e navegador.\n• **Dados de pagamento:** processados diretamente pelo Stripe; não armazenamos dados de cartão.`,
  },
  {
    title: "2. Finalidade do Tratamento",
    content: `Utilizamos seus dados para:\n\n• Prestação dos serviços contratados (gestão de grupos, IA, analytics).\n• Melhoria contínua dos algoritmos e modelos de IA.\n• Comunicações transacionais e de suporte.\n• Cumprimento de obrigações legais e regulatórias.\n• Marketing (somente com consentimento explícito).`,
  },
  {
    title: "3. Base Legal (LGPD Art. 7°)",
    content: `O tratamento de dados é realizado com base em:\n\n• **Consentimento** do titular (Art. 7°, I).\n• **Execução de contrato** ou procedimentos preliminares (Art. 7°, V).\n• **Interesse legítimo** do controlador (Art. 7°, IX).\n• **Cumprimento de obrigação legal** (Art. 7°, II).`,
  },
  {
    title: "4. Direitos do Titular",
    content: `Conforme a LGPD (Art. 18), você tem direito a:\n\n• Confirmação da existência de tratamento.\n• Acesso aos dados pessoais.\n• Correção de dados incompletos ou desatualizados.\n• Anonimização, bloqueio ou eliminação de dados.\n• Portabilidade dos dados.\n• Eliminação dos dados tratados com consentimento.\n• Revogação do consentimento.\n\nPara exercer qualquer direito, entre em contato pelo e-mail **dpo@syra.app** ou utilize o formulário abaixo.`,
  },
  {
    title: "5. Retenção de Dados",
    content: `Os dados são retidos pelo período necessário para:\n\n• Cumprimento das finalidades descritas nesta política.\n• Cumprimento de obrigações legais (mínimo 5 anos para dados fiscais).\n• Exercício regular de direitos em processo judicial.\n\nApós o término do tratamento, os dados serão eliminados ou anonimizados.`,
  },
  {
    title: "6. Compartilhamento",
    content: `Seus dados podem ser compartilhados com:\n\n• **Provedores de infraestrutura:** hospedagem, banco de dados e processamento.\n• **Provedores de IA:** para processamento de linguagem natural (dados anonimizados quando possível).\n• **Processadores de pagamento:** Stripe para transações financeiras.\n• **Autoridades legais:** quando exigido por lei ou ordem judicial.\n\nNão vendemos dados pessoais a terceiros.`,
  },
  {
    title: "7. Segurança",
    content: `Implementamos medidas técnicas e organizacionais para proteger seus dados:\n\n• Criptografia em trânsito (TLS 1.3) e em repouso (AES-256).\n• Isolamento multi-tenant com Row Level Security.\n• Controle de acesso baseado em papéis (RBAC).\n• Monitoramento contínuo e audit trail completo.\n• Backups automatizados com retenção configurável.`,
  },
  {
    title: "8. Contato do DPO",
    content: `Para questões relacionadas à proteção de dados:\n\n• **E-mail:** dpo@syra.app\n• **Endereço:** [Endereço da empresa]\n• **Autoridade Nacional:** Você também pode entrar em contato com a ANPD (Autoridade Nacional de Proteção de Dados).`,
  },
];

export default function Privacy() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background text-foreground">
      <nav className="fixed top-0 inset-x-0 z-50 glass-card border-b border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate("/")}>
            <MessageSquare className="w-7 h-7 text-primary" />
            <span className="text-xl font-bold font-headline tracking-tight text-primary">SYRA</span>
          </div>
          <Button variant="ghost" size="sm" onClick={() => navigate("/")}>
            <ArrowLeft className="mr-1 h-4 w-4" /> Voltar
          </Button>
        </div>
      </nav>

      <div className="pt-28 pb-20 px-4">
        <div className="max-w-3xl mx-auto">
          <motion.div className="text-center mb-12" initial="hidden" animate="visible" variants={fadeUp} custom={0}>
            <Shield className="h-10 w-10 text-primary mx-auto mb-4" />
            <h1 className="text-4xl sm:text-5xl font-extrabold font-headline mb-4">Política de Privacidade</h1>
            <p className="text-muted-foreground text-lg">Em conformidade com a Lei Geral de Proteção de Dados (LGPD - Lei 13.709/2018)</p>
            <p className="text-sm text-muted-foreground mt-2">Última atualização: {new Date().toLocaleDateString("pt-BR")}</p>
          </motion.div>

          <div className="space-y-6">
            {SECTIONS.map((section, i) => (
              <motion.div key={i} initial="hidden" animate="visible" variants={fadeUp} custom={i + 1}>
                <Card>
                  <CardContent className="p-6">
                    <h2 className="text-lg font-bold font-headline mb-3">{section.title}</h2>
                    <div className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line prose prose-sm dark:prose-invert max-w-none">
                      {section.content.split("**").map((part, j) =>
                        j % 2 === 1 ? <strong key={j} className="text-foreground">{part}</strong> : part
                      )}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
